import React, {FC, useEffect, useState, useRef, useMemo} from 'react'
import {Modal} from 'bootstrap'
import {KTSVG} from '../../../../_metronic/helpers'
import Select from 'react-select'
import {CommonPaginationModel} from '../../../../Utils/commonPagination'
import {usePresence, InterpreterPresence} from '../../../context/PresenceContext'

interface InterpreterData {
  userId: string
  name: string
  email: string
  mobileNumber: string
  availability_status: string
  languages: string[]
  ServiceTypes: string[]
  communicationType?: string
  hasDeviceToken: boolean
}

interface InterpreterModalProps {
  show: boolean
  onClose: () => void
}

export const InterpreterModal: FC<InterpreterModalProps> = ({show, onClose}) => {
  // Get interpreters from context
  const {interpreters, isLoading: isLoadingInterpreters, refreshInterpreters} = usePresence()

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // Filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [selectedAvailabilityStatus, setSelectedAvailabilityStatus] = useState<string | null>(null)
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([])
  const [selectedCommunicationTypes, setSelectedCommunicationTypes] = useState<string[]>([])
  const [sortBy, setSortBy] = useState<string>('')
  const [order, setOrder] = useState<string>('desc')
  const isInitialMount = useRef(true)
  
  // Expanded rows state - track which interpreter ID is expanded (only one at a time)
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null)

  // Filter options - static availability status options
  const availabilityStatusOptions = [
    {value: 'Available', label: 'Available'},
    {value: 'On Call', label: 'On Call'},
    {value: 'Offline', label: 'Offline'},
  ]

  // Extract filter options from interpreters data
  const languageOptions = useMemo(() => {
    const languageSet = new Set<string>()
    if (!Array.isArray(interpreters)) {
      return []
    }
    interpreters.forEach((interpreter) => {
      interpreter.languages.forEach((lang) => {
        languageSet.add(lang.name)
      })
    })
    return Array.from(languageSet)
      .sort()
      .map((lang) => ({value: lang, label: lang}))
  }, [interpreters])

  const serviceTypeOptions = useMemo(() => {
    const serviceTypeSet = new Set<string>()
    if (!Array.isArray(interpreters)) {
      return []
    }
    interpreters.forEach((interpreter) => {
      interpreter.languages.forEach((lang) => {
        lang.serviceTypes.forEach((type) => {
          serviceTypeSet.add(type)
        })
      })
    })
    return Array.from(serviceTypeSet)
      .sort()
      .map((type) => ({value: type, label: type}))
  }, [interpreters])

  const communicationTypeOptions = useMemo(() => {
    const commTypeSet = new Set<string>()
    if (!Array.isArray(interpreters)) {
      return []
    }
    interpreters.forEach((interpreter) => {
      interpreter.communicationTypes.forEach((type) => {
        commTypeSet.add(type)
      })
    })
    // Static communication type options with display labels
    const staticOptions = [
      {value: 'SCHEDULED_OPI', label: 'Scheduled Telephonic (OPI_ShdTelephonic)'},
      {value: 'ON_DEMAND_OPI', label: 'On-Demand Telephonic (OPI_OndemandTelephonic)'},
      {value: 'SCHEDULED_VRI', label: 'Scheduled Video Interpreting (VRI_ShdVideoInteroreting)'},
      {
        value: 'ON_DEMAND_VRI',
        label: 'On-Demand Video Interpreting (VRI_OndemandVideoInteroreting)',
      },
      // {value: 'CONSEC_OSI', label: 'Onsite Consecutive (OSI_OnsiteConsecutive)'},
      // {value: 'SIMULT_OSI', label: 'Onsite Simultaneous (OSI_OnsiteSimultaneous)'},
      // {value: 'OSI_ONSITE', label: 'Onsite (OSI_Onsite)'},
      // {value: 'THIRD_PART', label: 'Third Party Platform (Other_3rdPartyPlatform)'},
    ]
    // Filter to only show options that exist in the data
    return staticOptions.filter((opt) => commTypeSet.has(opt.value))
  }, [interpreters])

  // Convert InterpreterPresence to InterpreterData format
  const convertToInterpreterData = (interpreter: InterpreterPresence): InterpreterData => {
    // Extract all service types from all languages
    const allServiceTypes = interpreter.languages.flatMap((lang) => lang.serviceTypes)
    // Get first communication type or empty string
    const communicationType = interpreter.communicationTypes[0] || ''
    // Map status to availability_status format
    let availability_status: string = interpreter.status
    if (interpreter.status === 'Offline') {
      availability_status = 'Not Configured'
    } else if (interpreter.status === 'On Call') {
      availability_status = 'Busy'
    }

    return {
      userId: interpreter.userId || '',
      name: interpreter.name || '',
      email: interpreter.email || '',
      mobileNumber: interpreter.phone || '',
      availability_status,
      languages: interpreter.languages.map((lang) => lang.name),
      ServiceTypes: allServiceTypes,
      communicationType,
      hasDeviceToken: interpreter.hasDeviceToken ?? false,
    }
  }

  // Filter and paginate interpreters client-side
  const filteredAndPaginatedData = useMemo(() => {
    // Convert to InterpreterData format
    if (!Array.isArray(interpreters)) {
      return {
        data: [],
        totalItems: 0,
        totalPages: 0,
      }
    }
    let filtered = interpreters.map(convertToInterpreterData)

    // Apply search filter
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase()
      filtered = filtered.filter(
        (interpreter) =>
          interpreter.name.toLowerCase().includes(searchLower) ||
          interpreter.email.toLowerCase().includes(searchLower) ||
          interpreter.userId.toLowerCase().includes(searchLower) ||
          interpreter.mobileNumber.toLowerCase().includes(searchLower)
      )
    }

    // Apply availability status filter
    if (selectedAvailabilityStatus) {
      filtered = filtered.filter((interpreter) => {
        if (selectedAvailabilityStatus === 'Available') {
          return (
            interpreter.availability_status === 'Available' ||
            interpreter.availability_status === 'available'
          )
        } else if (selectedAvailabilityStatus === 'On Call') {
          return (
            interpreter.availability_status === 'Busy' ||
            interpreter.availability_status === 'busy' ||
            interpreter.availability_status === 'On Call'
          )
        } else if (selectedAvailabilityStatus === 'Offline') {
          return (
            interpreter.availability_status === 'Not Configured' ||
            interpreter.availability_status === 'offline'
          )
        }
        return true
      })
    }

    // Apply language filter
    if (selectedLanguages.length > 0) {
      filtered = filtered.filter((interpreter) =>
        selectedLanguages.some((lang) => interpreter.languages.includes(lang))
      )
    }

    // Apply service type filter
    if (selectedServiceTypes.length > 0) {
      filtered = filtered.filter((interpreter) =>
        selectedServiceTypes.some((type) => interpreter.ServiceTypes.includes(type))
      )
    }

    // Apply communication type filter
    if (selectedCommunicationTypes.length > 0) {
      filtered = filtered.filter((interpreter) => {
        // Get the original interpreter to access all communication types
        const originalInterpreter = interpreters.find((interp) => interp.userId === interpreter.userId)
        if (!originalInterpreter) return false
        
        // Check if any of the selected communication types match any of the interpreter's communication types
        return selectedCommunicationTypes.some((selectedType) =>
          originalInterpreter.communicationTypes.includes(selectedType)
        )
      })
    }

    // Apply sorting
    if (sortBy) {
      filtered.sort((a, b) => {
        let aValue: any = a[sortBy as keyof InterpreterData]
        let bValue: any = b[sortBy as keyof InterpreterData]

        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase()
          bValue = bValue.toLowerCase()
        }

        if (order === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
        }
      })
    }

    // Calculate pagination
    const totalItems = filtered.length
    const totalPages = Math.ceil(totalItems / rowsPerPage)
    const startIndex = (currentPage - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    const paginated = filtered.slice(startIndex, endIndex)

    return {
      data: paginated,
      totalItems,
      totalPages,
    }
  }, [
    interpreters,
    debouncedSearchTerm,
    selectedAvailabilityStatus,
    selectedLanguages,
    selectedServiceTypes,
    selectedCommunicationTypes,
    sortBy,
    order,
    currentPage,
    rowsPerPage,
  ])

  const interpreterData = filteredAndPaginatedData.data
  const totalItems = filteredAndPaginatedData.totalItems
  const totalPages = filteredAndPaginatedData.totalPages

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset to page 1 when filters change (except when modal first opens)
  useEffect(() => {
    if (show && !isInitialMount.current) {
      setCurrentPage(1)
    }
    if (show && isInitialMount.current) {
      isInitialMount.current = false
    }
  }, [
    show,
    debouncedSearchTerm,
    selectedAvailabilityStatus,
    selectedLanguages,
    selectedServiceTypes,
    selectedCommunicationTypes,
    sortBy,
    order,
  ])

  // Handle modal show/hide with Bootstrap
  useEffect(() => {
    const modalElement = document.getElementById('kt_interpreter_modal')
    if (!modalElement) return

    if (show) {
      const modal = new Modal(modalElement)
      modal.show()
    } else {
      const modalInstance = Modal.getInstance(modalElement)
      if (modalInstance) {
        modalInstance.hide()
      }
    }
  }, [show])

  // Ensure React `onClose` is called whenever the Bootstrap modal is fully hidden
  // (including when closed via backdrop click or ESC key), so parent state stays in sync
  useEffect(() => {
    const modalElement = document.getElementById('kt_interpreter_modal')
    if (!modalElement) return

    const handleHidden = () => {
      onClose()
    }

    modalElement.addEventListener('hidden.bs.modal', handleHidden)

    return () => {
      modalElement.removeEventListener('hidden.bs.modal', handleHidden)
    }
  }, [onClose])

  // Reset filters when modal opens
  useEffect(() => {
    if (show) {
      setSearchTerm('')
      setDebouncedSearchTerm('')
      setSelectedAvailabilityStatus(null)
      setSelectedLanguages([])
      setSelectedServiceTypes([])
      setSelectedCommunicationTypes([])
      setSortBy('')
      setOrder('desc')
      setCurrentPage(1)
      setRowsPerPage(10)
      setExpandedRowId(null) // Reset expanded row
      isInitialMount.current = true
    }
  }, [show])

  // Periodically refresh interpreters starting 2 minutes after open, then every 2 minutes while modal is open
  useEffect(() => {
    if (!show) {
      return
    }

    const intervalId = window.setInterval(() => {
      refreshInterpreters()
    }, 2 * 60 * 1000) // 2 minutes

    return () => {
      window.clearInterval(intervalId)
    }
  }, [show, refreshInterpreters])

  const handleCloseModal = () => {
    onClose()
  }

  // Toggle row expansion - only one row can be expanded at a time
  const toggleRowExpansion = (userId: string) => {
    setExpandedRowId((prev) => (prev === userId ? null : userId))
  }

  // Get original interpreter data by userId
  const getOriginalInterpreter = (userId: string): InterpreterPresence | undefined => {
    if (!Array.isArray(interpreters)) {
      return undefined
    }
    return interpreters.find((interp) => interp.userId === userId)
  }

  // Get communication type display label - extract only the value part (before the parenthesis)
  const getCommunicationTypeLabel = (type: string): string => {
    const option = communicationTypeOptions.find((opt) => opt.value === type)
    if (option) {
      // Extract only the part before the parenthesis (e.g., "Scheduled Telephonic" from "Scheduled Telephonic (OPI_ShdTelephonic)")
      const label = option.label
      const valuePart = label.split('(')[0].trim()
      return valuePart || type
    }
    return type
  }

  return (
    <div className='modal fade' tabIndex={-1} id='kt_interpreter_modal'>
      <div className='modal-dialog modal-xl modal-dialog-centered'>
        <div className='modal-content'>
          <div className='modal-header py-2'>
            <h4 className='modal-title'>Available Interpreters</h4>
            <div
              className='btn btn-icon btn-sm btn-active-light-primary ms-2'
              data-bs-dismiss='modal'
              aria-label='Close'
              onClick={handleCloseModal}
            >
              <KTSVG
                path='/media/icons/duotune/arrows/arr061.svg'
                className='svg-icon svg-icon-2x'
              />
            </div>
          </div>
          <div className='modal-body'>
            {/* Filters Section */}
            {/* First Row - Search */}
            <div className='row g-3 mb-3'>
              <div className='col-sm-12 col-md-12 col-lg-12'>
                <label className='form-label fs-7 mb-1'>Search</label>
                <input
                  type='text'
                  className='form-control form-control-sm'
                  placeholder='Search by name, email, ID, or mobile number...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{height: '38px'}}
                />
              </div>
            </div>

            {/* Second Row - Other Filters */}
            <div className='row g-3 mb-5'>
              <div className='col-sm-12 col-md-6 col-lg-3'>
                <label className='form-label fs-7 mb-1'>Availability Status</label>
                <Select
                  className='react-select-styled react-select-solid react-select-sm'
                  classNamePrefix='react-select'
                  options={availabilityStatusOptions}
                  placeholder='Select status'
                  value={
                    selectedAvailabilityStatus
                      ? availabilityStatusOptions.find(
                          (opt) => opt.value === selectedAvailabilityStatus
                        )
                      : null
                  }
                  onChange={(option: any) =>
                    setSelectedAvailabilityStatus(option ? option.value : null)
                  }
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition='fixed'
                  styles={{
                    control: (provided: any) => ({
                      ...provided,
                      width: '100%',
                      border: '1px solid #e4e6ef',
                    }),
                    menuPortal: (provided: any) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                  }}
                />
              </div>
              <div className='col-sm-12 col-md-6 col-lg-3'>
                <label className='form-label fs-7 mb-1'>Languages</label>
                <Select
                  className='react-select-styled react-select-solid react-select-sm'
                  classNamePrefix='react-select'
                  options={languageOptions}
                  placeholder='Select languages'
                  isMulti
                  value={languageOptions.filter((opt) => selectedLanguages.includes(opt.value))}
                  onChange={(options: any) =>
                    setSelectedLanguages(options ? options.map((opt: any) => opt.value) : [])
                  }
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition='fixed'
                  styles={{
                    control: (provided: any) => ({
                      ...provided,
                      width: '100%',
                      border: '1px solid #e4e6ef',
                    }),
                    menuPortal: (provided: any) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                  }}
                />
              </div>
              <div className='col-sm-12 col-md-6 col-lg-3'>
                <label className='form-label fs-7 mb-1'>Service Types</label>
                <Select
                  className='react-select-styled react-select-solid react-select-sm'
                  classNamePrefix='react-select'
                  options={serviceTypeOptions || []}
                  placeholder='Select service types'
                  isMulti
                  value={serviceTypeOptions.filter((opt) =>
                    selectedServiceTypes.includes(opt.value)
                  )}
                  onChange={(options: any) =>
                    setSelectedServiceTypes(options ? options.map((opt: any) => opt.value) : [])
                  }
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition='fixed'
                  styles={{
                    control: (provided: any) => ({
                      ...provided,
                      width: '100%',
                      border: '1px solid #e4e6ef',
                    }),
                    menuPortal: (provided: any) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                  }}
                />
              </div>
              <div className='col-sm-12 col-md-6 col-lg-3'>
                <label className='form-label fs-7 mb-1'>Communication Type</label>
                <Select
                  className='react-select-styled react-select-solid react-select-sm'
                  classNamePrefix='react-select'
                  options={communicationTypeOptions}
                  placeholder='Select communication types'
                  isMulti
                  value={communicationTypeOptions.filter((opt) =>
                    selectedCommunicationTypes.includes(opt.value)
                  )}
                  onChange={(options: any) =>
                    setSelectedCommunicationTypes(
                      options ? options.map((opt: any) => opt.value) : []
                    )
                  }
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition='fixed'
                  styles={{
                    control: (provided: any) => ({
                      ...provided,
                      width: '100%',
                      border: '1px solid #e4e6ef',
                    }),
                    menuPortal: (provided: any) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                  }}
                />
              </div>
            </div>

            <div className='table-responsive' style={{maxHeight: '600px', overflowY: 'auto'}}>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-2 gy-2'>
                <thead style={{position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10}}>
                  <tr className='fw-semibold text-muted'>
                    <th className='min-w-150px text-uppercase'>User ID</th>
                    <th className='min-w-150px text-uppercase'>Name</th>
                    <th className='min-w-200px text-uppercase'>Email</th>
                    <th className='min-w-150px text-uppercase'>Mobile Number</th>
                    <th className='min-w-150px text-uppercase'>Availability Status</th>
                    <th className='min-w-120px text-uppercase'>Device Token</th>
                    <th className='min-w-100px text-uppercase text-end'>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingInterpreters ? (
                    <tr>
                      <td colSpan={7}>
                        <div className='py-5 d-flex flex-column align-content-center justify-content-center'>
                          <div className='text-center'>
                            <div className='spinner-border text-primary' role='status'>
                              <span className='visually-hidden'>Loading...</span>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : interpreterData.length > 0 ? (
                    interpreterData.map((interpreter, index) => {
                      const isExpanded = expandedRowId === interpreter.userId
                      const originalInterpreter = getOriginalInterpreter(interpreter.userId)
                      
                      return (
                        <React.Fragment key={index}>
                          <tr>
                            <td>
                              <span className='text-gray-800 d-block fs-7'>
                                {interpreter?.userId ?? 'N/A'}
                              </span>
                            </td>
                            <td>
                              <span className='text-gray-800 d-block fs-7'>{interpreter.name}</span>
                            </td>
                            <td>
                              <span className='text-gray-800 d-block fs-7'>{interpreter.email}</span>
                            </td>
                            <td>
                              <span className='text-gray-800 d-block fs-7'>
                                {interpreter.mobileNumber}
                              </span>
                            </td>
                            <td>
                              {(() => {
                                const status = interpreter.availability_status
                                let displayText = status
                                let badgeClass = 'badge-secondary'

                                if (status === 'Not Configured') {
                                  displayText = 'Offline'
                                  badgeClass = 'badge-secondary' // muted color
                                } else if (status === 'Available' || status === 'available') {
                                  displayText = 'Available'
                                  badgeClass = 'badge-success' // green
                                } else if (
                                  status === 'Busy' ||
                                  status === 'busy' ||
                                  status === 'On Call' ||
                                  status === 'on call'
                                ) {
                                  displayText = 'On Call'
                                  badgeClass = 'badge-danger' // red
                                }

                                return <span className={`badge ${badgeClass}`}>{displayText}</span>
                              })()}
                            </td>
                            <td>
                              <span className='text-gray-800 d-block fs-7'>
                                {interpreter.hasDeviceToken ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td className='text-end'>
                              <button
                                className='btn btn-sm btn-icon btn-light-primary'
                                onClick={() => toggleRowExpansion(interpreter.userId)}
                                title={isExpanded ? 'Hide Details' : 'Show Details'}
                              >
                                {isExpanded ? (
                                  <KTSVG
                                    path='/media/icons/duotune/arrows/arr072.svg'
                                    className='svg-icon-3'
                                  />
                                ) : (
                                  <KTSVG
                                    path='/media/icons/duotune/general/gen052.svg'
                                    className='svg-icon-3'
                                  />
                                )}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && originalInterpreter && (
                            <tr>
                              <td colSpan={7} className='p-0'>
                                <div className='p-6 bg-light' style={{borderTop: '2px solid #e4e6ef'}}>
                                  <div className='row g-4'>
                                    {/* Languages with Service Types Section */}
                                    <div className='col-12'>
                                      <div className='d-flex align-items-center mb-4'>
                                        <h6 className='fw-bold text-gray-800 mb-0 fs-5'>Languages & Service Types</h6>
                                      </div>
                                      {originalInterpreter.languages.length > 0 ? (
                                        <div className='table-responsive'>
                                          <table className='table table-bordered align-middle' style={{border: '1px solid #e4e6ef'}}>
                                            <thead>
                                              <tr className='fw-bold text-gray-700'>
                                                <th className='min-w-200px' style={{border: '1px solid #e4e6ef'}}>Language</th>
                                                <th style={{border: '1px solid #e4e6ef'}}>Service Types</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {originalInterpreter.languages.map((lang, langIdx) => (
                                                <tr key={langIdx}>
                                                  <td className='align-middle' style={{border: '1px solid #e4e6ef'}}>
                                                    <span className='text-gray-800 fs-6 fw-semibold'>
                                                      {lang.name}
                                                    </span>
                                                  </td>
                                                  <td style={{border: '1px solid #e4e6ef'}}>
                                                    {lang.serviceTypes.length > 0 ? (
                                                      <div className='d-flex flex-wrap gap-2'>
                                                        {lang.serviceTypes.map((serviceType, stIdx) => (
                                                          <span
                                                            key={stIdx}
                                                            className='badge badge-dark fs-6 px-3 py-2'
                                                          >
                                                            {serviceType}
                                                          </span>
                                                        ))}
                                                      </div>
                                                    ) : (
                                                      <span className='text-muted fs-7'>No service types</span>
                                                    )}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : (
                                        <div className='text-center text-muted fs-7' style={{padding: 0, margin: 0}}>
                                          No languages available
                                        </div>
                                      )}
                                    </div>

                                    {/* Communication Types Section */}
                                    <div className='col-12'>
                                      <div className='d-flex align-items-center mb-3'>
                                        <h6 className='fw-bold text-gray-800 mb-0 fs-5'>Communication Types</h6>
                                      </div>
                                      {originalInterpreter.communicationTypes.length > 0 ? (
                                        <div className='d-flex flex-wrap gap-2'>
                                          {originalInterpreter.communicationTypes.map(
                                            (commType, commIdx) => (
                                              <span
                                                key={commIdx}
                                                className='badge badge-success fs-6 px-3 py-2'
                                              >
                                                {getCommunicationTypeLabel(commType)}
                                              </span>
                                            )
                                          )}
                                        </div>
                                      ) : (
                                        <div className='text-center text-muted fs-7' style={{padding: 0, margin: 0}}>
                                          No communication types available
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan={7}>
                        <div className='py-5 d-flex flex-column align-content-center justify-content-center'>
                          <div className='text-center'>
                            <div className='symbol symbol-100px'>
                              <img src='/media/other/nodata.png' alt='' />
                            </div>
                          </div>
                          <div className='d-flex text-center w-100 align-content-center justify-content-center fw-semibold fs-6 text-gray-400'>
                            No interpreters found
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalItems > 0 && (
              <div className='mt-4'>
                <CommonPaginationModel
                  currentPage={currentPage}
                  totalPages={totalPages}
                  rowsPerPage={rowsPerPage}
                  total={totalItems}
                  onPageChange={(page: number) => setCurrentPage(page)}
                  onRowsPerPageChange={(newRowsPerPage: number) => {
                    setRowsPerPage(newRowsPerPage)
                    setCurrentPage(1)
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
